import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Divider, TextArea, Typography } from '@telus-uds/components-web';

import { Box } from '../../design/css';
import request, { isAdmin, isSalesRep } from '../../utils/request';
import { orderStatus as statusConstants } from '../../utils/constant';
import ValidateLoader from '../../UI/validateLoader';
import MessageNotification from '../../common/messageNotification';
import OrderAction from './components/orderAction';
import OrderAttachments from './components/orderAttachments';
import OrderComments from './components/orderComments';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import OrderHeader from './components/orderHeader';
import OrderButton from './components/orderButton';
import OrderHistory from './components/orderHistory';
import ContractEmails from '../../design/contract-emails';

const UpdateOrder = ({
  setOpen,
  open,
  order,
  onSave,
  users,
  userRights,
  children,
  comments,
  hideQuote
}) => {
  const {
    incomplete,
    contractSigned,
    contractSent,
    validated,
    orderCanceled,
    orderSubmitted
  } = statusConstants;
  const {
    id: orderId,
    status: orderStatus,
    pdfLinks,
    doesTheCustomerRequireWhiteGlove,
    wgInstallationDate: wgIntallDate
  } = order;
  const [status, setStatus] = useState(orderStatus || '');
  const [actionChanged, setActionChanged] = useState(false);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const [defaultAssignee, setDefaultAssignee] = useState([]);
  const [assignee, setAssignee] = useState(defaultAssignee);
  const [isOrderValidating, setIsOrderValidating] = useState(false);
  const [wgInstallationDate, setWgInstallationDate] = useState(wgIntallDate);
  const [contractEmailPopUp, setContractEmailPopUp] = useState(false);

  const isValidated = orderStatus === validated;
  const isDisabled = [contractSigned, orderCanceled].includes(orderStatus);
  const isStatusChanged = status !== orderStatus || status === contractSent;
  const isAssigneeUpdated = defaultAssignee !== assignee;
  const isOrderUpdated =
    isStatusChanged || comment || isAssigneeUpdated || wgInstallationDate;

  const localizedString = useLocalizedStringsContext();
  const {
    ORDERS: {
      add,
      save,
      loadingContentMessage,
      ORDER_SUMMARY: orderSummary,
      ORDER_COMMENTS: orderComments,
      orderQuestion,
      orderHistory
    }
  } = localizedString;

  useEffect(() => {
    if (users && order.assignedUserId && !assignee.length) {
      const currentAssignee = users.filter(
        user => user.id === order.assignedUserId
      );
      setDefaultAssignee(currentAssignee);
      setAssignee(currentAssignee);
    } else if (order.orderAssignedTo && !assignee.length) {
      const {
        orderAssignedTo: { id, first_name: firstName, last_name: lastName }
      } = order;
      const currentAssignee = [
        {
          id,
          label: `${firstName} ${lastName}`
        }
      ];
      setDefaultAssignee(currentAssignee);
      setAssignee(currentAssignee);
    }
  }, [order, users]);

  useEffect(() => {
    setError(false);
  }, [status, comment]);

  const getAssigneeId = () => {
    if (!isAssigneeUpdated) return undefined;
    return assignee.length ? assignee[0].id : -1;
  };

  const addComment = () => {
    const commentsBody = {
      text: comment
    };
    return request.post(`/orders/${order.id}/comments`, commentsBody);
  };

  const closeModal = needToClose => {
    if (isOrderUpdated && !error) {
      if (onSave) {
        onSave({ isUpdated: true, orderId: order.orderId });
      }
      if (needToClose) setOpen(!open);
    }
  };

  const updateOrder = ccEmailData => {
    const updateBody = {
      step: status === incomplete ? 1 : undefined
    };
    if (isStatusChanged) updateBody.status = status;
    if (isAssigneeUpdated) updateBody.assignedUserId = getAssigneeId();
    if (wgInstallationDate) updateBody.wgInstallationDate = wgInstallationDate;
    if (status === statusConstants.contractSent) {
      updateBody.isContractResend = false;
      if (ccEmailData !== null) {
        updateBody.contractEmail = ccEmailData.contractEmail;
        updateBody.contractEmailCC = ccEmailData.contractEmailCC;
        closeModal(true);
      }
    }
    setLoading(true);
    if (status === statusConstants.waitingOnSales)
      updateBody.isOrderReturned = false;
    return request.put(`/orders/${order.id}`, updateBody).then(() => {
      window.location.reload();
      setLoading(false);
    });
  };

  const onStatusChange = e => {
    setActionChanged(true);
    setStatus(e.target.value);
  };

  const onClickSave = async (needToClose = true) => {
    if (!isOrderUpdated) return;
    try {
      setIsOrderValidating(true);
      setLoading(true);
      if (comment) {
        await addComment();
        closeModal(needToClose);
      }

      if (isStatusChanged || isAssigneeUpdated || wgInstallationDate) {
        if (isStatusChanged && status === statusConstants.contractSent) {
          setContractEmailPopUp(true);
        } else {
          await updateOrder();
          closeModal(needToClose);
        }
      }
      setIsOrderValidating(false);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setIsOrderValidating(false);
      setLoading(false);
    }
  };

  const button = {
    label: save,
    onClick: onClickSave,
    variant: 'purple',
    isVisibleOnValidate: false
  };

  return (
    <Box bottom={4}>
      {loading ? (
        <ValidateLoader message={loadingContentMessage} />
      ) : (
        <>
          <div style={{ padding: '0.75rem' }}>
            <OrderHeader userRights={userRights} order={order} />
          </div>
          <Divider space={4} variant={{ inverse: true }} />
          <Typography heading="h2" variant={{ size: 'h2' }} block>
            {orderSummary}
          </Typography>
          <Box>{children}</Box>
          <OrderAttachments
            links={pdfLinks}
            orderData={order}
            hideQuote={hideQuote}
          />
          <>
            <Divider space={4} variant={{ inverse: true }} />
            <Typography heading="h2" variant={{ size: 'h2' }} block>
              {orderHistory}
            </Typography>
            <OrderHistory orderId={orderId} />
          </>
          {((comments && comments.length > 0) ||
            (userRights.canAddComments && !isDisabled)) && (
            <>
              <Divider space={4} variant={{ inverse: true }} />
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                {orderComments}
              </Typography>
              {comments && comments.length > 0 && (
                <OrderComments data={comments} />
              )}
              {userRights.canAddComments && !isDisabled && (
                <>
                  <Box width={100} pt={16} pb={16}>
                    <TextArea
                      label={add}
                      id="order-validation"
                      value={comment}
                      onChange={(value) => setComment(value)}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" width={100}>
                    <OrderButton
                      button={button}
                      isOrderValidating={isOrderValidating}
                      isValidated={isValidated}
                    />
                  </Box>
                </>
              )}
            </>
          )}
          <ContractEmails
            signingAuthorityEmail={order.email_signingAuthority || ''}
            open={contractEmailPopUp}
            setOpen={setContractEmailPopUp}
            setData={data => {
              updateOrder(data);
            }}
          />
          {(isSalesRep || isAdmin) && order && order.status !== orderSubmitted && (
            <>
              <Divider space={4} variant={{ inverse: true }} />
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                {orderQuestion}
              </Typography>
              {error && <MessageNotification variant="error" message={error} />}
              {
                <OrderAction
                  order={order}
                  button={button}
                  orderStatus={orderStatus}
                  status={status}
                  channelName={order.channelName}
                  onStatusChange={onStatusChange}
                  actionChanged={actionChanged}
                  isOrderValidating={isOrderValidating}
                  isValidated={isValidated}
                  whiteGloveRequired={doesTheCustomerRequireWhiteGlove}
                  wgInstallationDate={wgInstallationDate}
                  setWgInstallationDate={setWgInstallationDate}
                />
              }
            </>
          )}
        </>
      )}
    </Box>
  );
};

UpdateOrder.propTypes = {
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  order: PropTypes.shape({
    id: PropTypes.number,
    orderId: PropTypes.string,
    status: PropTypes.string,
    channelName: PropTypes.string,
    businessLegalName: PropTypes.string,
    assignedUserId: PropTypes.number,
    orderAssignedTo: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string
    }),
    pdfLinks: PropTypes.arrayOf(
      PropTypes.shape({
        quotePdf: PropTypes.string,
        contractUnSigned: PropTypes.string,
        contractSignedPdf: PropTypes.string
      })
    ),
    doesTheCustomerRequireWhiteGlove: PropTypes.string,
    wgInstallationDate: PropTypes.string,
    email_signingAuthority: PropTypes.string
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number
    })
  ).isRequired,
  userRights: PropTypes.shape({
    canAssignOrder: PropTypes.bool,
    isStatusEditable: PropTypes.bool,
    canAddComments: PropTypes.bool,
    canPerformAction: PropTypes.bool
  }).isRequired,
  children: PropTypes.node.isRequired,
  comments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hideQuote: PropTypes.bool
};

UpdateOrder.defaultProps = {
  hideQuote: false
};

export default UpdateOrder;
